import { useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { type Dayjs } from 'dayjs';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { authApi } from '../api/auth';
import { OfflineBanner } from '../components/OfflineBanner';
import { PasswordStrengthChecklist } from '../components/PasswordStrengthChecklist';
import { evaluatePasswordStrength } from '../utils/passwordStrength';

interface AcceptInviteForm {
  name: string;
  surname: string;
  dateOfBirth: Dayjs | null;
  password: string;
  confirmPassword: string;
}

const STEPS = ['I tuoi dati', 'Imposta password'];

export function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [serverError, setServerError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    control,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm<AcceptInviteForm>({
    defaultValues: { name: '', surname: '', dateOfBirth: null, password: '', confirmPassword: '' },
    mode: 'onBlur',
  });

  const passwordWatch = watch('password');
  const passwordOk = useMemo(
    () => evaluatePasswordStrength(passwordWatch ?? '').isValid,
    [passwordWatch],
  );

  if (!token) {
    return (
      <Container maxWidth="xs" sx={{ pt: 8 }}>
        <Alert severity="error">Token di invito mancante. Richiedi un nuovo invito.</Alert>
      </Container>
    );
  }

  if (success) {
    return (
      <Container maxWidth="xs" sx={{ pt: 8, textAlign: 'center' }}>
        <CheckCircleOutlineIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          Account attivato!
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Puoi ora accedere con l&apos;email dell&apos;invito e la password appena impostata.
        </Typography>
        <Button variant="contained" onClick={() => navigate('/login')}>
          Vai al login
        </Button>
      </Container>
    );
  }

  const goNext = async () => {
    setServerError(null);
    const ok = await trigger(['name', 'surname', 'dateOfBirth']);
    if (ok) setActiveStep(1);
  };

  const onSubmit = async (data: AcceptInviteForm) => {
    setServerError(null);
    if (!evaluatePasswordStrength(data.password).isValid) {
      setServerError('La password non soddisfa i requisiti di sicurezza.');
      return;
    }
    try {
      await authApi.acceptInvite({
        token,
        password: data.password,
        name: data.name.trim(),
        surname: data.surname.trim(),
        dateOfBirth: data.dateOfBirth ? data.dateOfBirth.format('YYYY-MM-DD') : '',
      });
      setSuccess(true);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Invito non valido o scaduto.';
      setServerError(msg);
    }
  };

  return (
    <>
      <OfflineBanner />
      <Container
        maxWidth="xs"
        sx={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', py: 4 }}
      >
        <Paper elevation={3} sx={{ p: 4, width: '100%', borderRadius: 3 }}>
          <Box textAlign="center" mb={2}>
            <Typography variant="h5" fontWeight={700} color="primary">
              Attiva il tuo account
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Completa i dati e scegli una password per accedere a Cantir.
            </Typography>
          </Box>

          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            {activeStep === 0 && (
              <>
                <TextField
                  label="Nome *"
                  size="small"
                  autoFocus
                  {...register('name', { required: 'Nome obbligatorio' })}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                />

                <TextField
                  label="Cognome *"
                  size="small"
                  {...register('surname', { required: 'Cognome obbligatorio' })}
                  error={!!errors.surname}
                  helperText={errors.surname?.message}
                />

                <Controller
                  name="dateOfBirth"
                  control={control}
                  rules={{ required: 'Data di nascita obbligatoria' }}
                  render={({ field, fieldState }) => (
                    <DatePicker
                      label="Data di nascita *"
                      value={field.value}
                      onChange={field.onChange}
                      maxDate={dayjs()}
                      slotProps={{
                        textField: {
                          size: 'small',
                          fullWidth: true,
                          error: !!fieldState.error,
                          helperText: fieldState.error?.message,
                        },
                      }}
                    />
                  )}
                />

                {serverError && (
                  <Alert severity="error" variant="outlined" sx={{ py: 0.5 }}>
                    {serverError}
                  </Alert>
                )}

                <Button variant="contained" size="large" onClick={() => void goNext()}>
                  Continua
                </Button>
              </>
            )}

            {activeStep === 1 && (
              <>
                <Typography variant="subtitle2" color="text.secondary">
                  Imposta la password che userai per accedere.
                </Typography>

                <TextField
                  label="Password"
                  type="password"
                  size="small"
                  autoFocus
                  autoComplete="new-password"
                  {...register('password', {
                    required: 'Password obbligatoria',
                    validate: (val) =>
                      evaluatePasswordStrength(val).isValid ||
                      'La password non soddisfa i requisiti di sicurezza',
                  })}
                  error={!!errors.password}
                  helperText={errors.password?.message}
                />

                <PasswordStrengthChecklist password={passwordWatch ?? ''} />

                <TextField
                  label="Conferma password"
                  type="password"
                  size="small"
                  autoComplete="new-password"
                  {...register('confirmPassword', {
                    required: 'Conferma la password',
                    validate: (val) =>
                      val === watch('password') || 'Le password non coincidono',
                  })}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword?.message}
                />

                {serverError && (
                  <Alert severity="error" variant="outlined" sx={{ py: 0.5 }}>
                    {serverError}
                  </Alert>
                )}

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    type="button"
                    variant="outlined"
                    size="large"
                    disabled={isSubmitting}
                    onClick={() => {
                      setServerError(null);
                      setActiveStep(0);
                    }}
                    sx={{ flex: 1 }}
                  >
                    Indietro
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={isSubmitting || !passwordOk}
                    startIcon={
                      isSubmitting ? <CircularProgress size={18} color="inherit" /> : null
                    }
                    sx={{ flex: 1 }}
                  >
                    {isSubmitting ? 'Attivazione…' : 'Attiva account'}
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Paper>
      </Container>
    </>
  );
}
